/**
 * Race a promise against a timer.
 *
 * Resolves with `'timeout'` if `ms` elapses first; otherwise resolves
 * (or rejects) with the inner promise's outcome. The timer is always
 * cleared once the race settles, and a no-op rejection handler is
 * attached to the inner promise so a late rejection (after the timer
 * has already won the race) does not surface as an unhandled
 * promise rejection.
 *
 * The `label` is emitted via `console.warn` from inside the timer
 * callback so the source of the timeout is attributable in dev tools,
 * Xcode / Android logcat, or any captured console stream — even though
 * by the time the timer fires the original call stack has unwound. It
 * is the only logging the helper does on its own; callers remain
 * responsible for any structured (e.g. Datadog) logging they need.
 *
 * @example
 *   const outcome = await withTimeout(
 *     somethingSlow(),
 *     5_000,
 *     'somethingSlow',
 *   );
 *   if (outcome === 'timeout') {
 *     logToDatadog('error', `[MY_PREFIX] slow op timed out after 5000ms`);
 *     return fallback;
 *   }
 *   // outcome is T here
 */
export const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T | 'timeout'> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => {
      console.warn(`[withTimeout] '${label}' timed out after ${ms}ms`);
      resolve('timeout');
    }, ms);
  });

  // Observe the inner promise so that if it settles AFTER the timer has
  // already won the race, a late rejection doesn't surface as an
  // unhandled promise rejection. Rejections that lose to the timer are
  // intentionally swallowed here — the caller has already moved on with
  // the `'timeout'` outcome. Rejections that win the race are still
  // propagated by `Promise.race` below.
  promise.catch(() => undefined);

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
};
